import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { useAuth } from '../hooks/useAuth';
import SEO from '../components/SEO';

const RegisterPage = () => {
  const { register, googleLogin } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1 = choose role, 2 = fill form
  const [role, setRole] = useState('');
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', phone: '', password: '', confirm_password: '', business_name: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password !== form.confirm_password) return setError('Passwords do not match');
    if (role === 'provider' && !form.business_name.trim()) return setError('Business name is required for providers');
    setLoading(true);
    try {
      const { confirm_password, business_name, ...data } = form;
      const payload = { ...data, role };
      if (role === 'provider') {
        payload.provider = { business_name };
      }
      await register(payload);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.errors?.[0]?.msg || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setError('');
    setLoading(true);
    try {
      const result = await googleLogin(credentialResponse.credential);
      navigate((result.isNewUser || result.user?.role === 'pending') ? '/complete-profile' : '/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Google sign-up failed');
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "w-full border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 outline-none transition-all text-sm";

  const roles = [
    {
      value: 'customer',
      title: 'Buyer / Client',
      desc: 'Browse services, buy products, and book appointments',
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
        </svg>
      ),
      color: 'primary',
    },
    {
      value: 'provider',
      title: 'Business / Provider',
      desc: 'Offer services, sell products, and grow your business',
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 21v-7.5a.75.75 0 01.75-.75h3a.75.75 0 01.75.75V21m-4.5 0H2.36m11.14 0H18m0 0h3.64m-1.39 0V9.349m-16.5 11.65V9.35m0 0a3.001 3.001 0 003.75-.615A2.993 2.993 0 009.75 9.75c.896 0 1.7-.393 2.25-1.016a2.993 2.993 0 002.25 1.016c.896 0 1.7-.393 2.25-1.016a3.001 3.001 0 003.75.614m-16.5 0a3.004 3.004 0 01-.621-4.72L4.318 3.44A1.5 1.5 0 015.378 3h13.243a1.5 1.5 0 011.06.44l1.19 1.189a3 3 0 01-.621 4.72m-13.5 8.65h3.75a.75.75 0 00.75-.75V13.5a.75.75 0 00-.75-.75H6.75a.75.75 0 00-.75.75v3.15c0 .414.336.75.75.75z" />
        </svg>
      ),
      color: 'green',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-primary-50/30 flex items-center justify-center px-4 py-12">
      <SEO title="Register" description="Create your free TopInfo Rwanda account. Start buying, selling products, and booking services across Rwanda today." keywords="register TopInfo Rwanda, create account, sign up marketplace Rwanda, join TopInfo, sell services Rwanda, become provider Kigali" />
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-1 mb-4">
            <span className="text-3xl font-extrabold text-primary-600">Top</span>
            <span className="text-3xl font-extrabold text-rwanda-green">Info</span>
          </Link>
          <p className="text-gray-500">{step === 1 ? 'How will you use TopInfo?' : 'Create your account'}</p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl shadow-black/5 border border-gray-100 p-8">
          {error && (
            <div className="bg-red-50 text-red-600 p-3.5 rounded-xl mb-5 text-sm font-medium border border-red-100">{error}</div>
          )}

          {/* Step 1: Choose Role */}
          {step === 1 && (
            <div>
              <div className="space-y-3 mb-6">
                {roles.map((r) => (
                  <button
                    key={r.value}
                    type="button"
                    onClick={() => setRole(r.value)}
                    className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all text-left ${
                      role === r.value
                        ? `border-${r.color}-500 bg-${r.color}-50/50 ring-2 ring-${r.color}-500/20`
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className={`flex-shrink-0 ${role === r.value ? `text-${r.color}-600` : 'text-gray-400'}`}>
                      {r.icon}
                    </div>
                    <div className="flex-1">
                      <div className={`font-semibold text-sm ${role === r.value ? `text-${r.color}-700` : 'text-gray-700'}`}>
                        {r.title}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">{r.desc}</div>
                    </div>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                      role === r.value ? `border-${r.color}-500 bg-${r.color}-500` : 'border-gray-300'
                    }`}>
                      {role === r.value && (
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                  </button>
                ))}
              </div>

              <button
                onClick={() => { if (role) { setError(''); setStep(2); } else { setError('Please select how you want to use TopInfo'); } }}
                className="w-full bg-primary-600 text-white py-3 rounded-xl font-semibold hover:bg-primary-700 transition-all shadow-md shadow-primary-500/20 text-sm disabled:opacity-50"
                disabled={!role}
              >
                Continue
              </button>

              <div className="relative my-5">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200"></div></div>
                <div className="relative flex justify-center text-sm"><span className="bg-white px-4 text-gray-400">or sign up with</span></div>
              </div>

              <div className="flex justify-center">
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={() => setError('Google sign-up failed')}
                  size="large"
                  width="100%"
                  text="signup_with"
                  shape="pill"
                />
              </div>
            </div>
          )}

          {/* Step 2: Registration Form */}
          {step === 2 && (
            <div>
              {/* Role Badge */}
              <div className="flex items-center justify-between mb-5">
                <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold ${
                  role === 'provider' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-primary-50 text-primary-700 border border-primary-200'
                }`}>
                  {role === 'provider' ? '🏪 Business Account' : '👤 Personal Account'}
                </div>
                <button onClick={() => setStep(1)} className="text-sm text-primary-600 hover:text-primary-700 font-medium">
                  Change
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">First Name</label>
                    <input type="text" required value={form.first_name}
                      onChange={(e) => setForm({ ...form, first_name: e.target.value })} className={inputClass} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Last Name</label>
                    <input type="text" required value={form.last_name}
                      onChange={(e) => setForm({ ...form, last_name: e.target.value })} className={inputClass} />
                  </div>
                </div>

                {/* Business Name - Only for providers */}
                {role === 'provider' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Business Name <span className="text-red-500">*</span>
                    </label>
                    <input type="text" required value={form.business_name}
                      onChange={(e) => setForm({ ...form, business_name: e.target.value })} className={inputClass}
                      placeholder="e.g., John's Tech Solutions" />
                    <p className="text-xs text-gray-400 mt-1">This will be displayed on your public profile</p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
                  <input type="email" required value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputClass} placeholder="you@example.com" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone Number</label>
                  <input type="tel" required value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputClass} placeholder="+250 7XX XXX XXX" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
                  <input type="password" required value={form.password} minLength={8}
                    onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputClass} placeholder="At least 8 characters" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Confirm Password</label>
                  <input type="password" required value={form.confirm_password}
                    onChange={(e) => setForm({ ...form, confirm_password: e.target.value })} className={inputClass} />
                </div>
                <button type="submit" disabled={loading}
                  className="w-full bg-primary-600 text-white py-3 rounded-xl font-semibold hover:bg-primary-700 disabled:opacity-50 transition-all shadow-md shadow-primary-500/20 hover:shadow-lg hover:shadow-primary-500/30 text-sm">
                  {loading ? 'Creating account...' : role === 'provider' ? 'Create Business Account' : 'Create Account'}
                </button>
              </form>
            </div>
          )}

          <p className="text-center text-sm text-gray-500 mt-6">
            Already have an account?{' '}
            <Link to="/login" className="text-primary-600 font-semibold hover:text-primary-700">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
